import React from 'react'

import { Box, Typography } from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import { CheckCircle, Cancel, RemoveCircle, Stars } from '@material-ui/icons'
import clsx from 'clsx'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'

import courseConstants from 'constants/course'
import useStyles from './styles'

const AttemptDetails = ({ attempt, summaryTitle, attemptScore }) => {
  const classes = useStyles()
  const {
    LETTERS,
    ANSWERS_RESULT_CORRECT,
    ANSWERS_RESULT_INCORRECT,
    ANSWERS_RESULT_PARTIAL,
    ANSWERS_RESULT_UNANSWERED,
  } = courseConstants

  const answersReview = attempt?.answersReview || []
  const totalQuestions = isEmpty(answersReview)
    ? attempt?.totalQuestions ?? 0
    : answersReview.length
  const correctCount = isEmpty(answersReview)
    ? attempt?.correctCount ?? 0
    : answersReview.filter(
        (question) => question?.result === ANSWERS_RESULT_CORRECT,
      ).length
  const partialCount = answersReview.filter(
    (question) => question?.result === ANSWERS_RESULT_PARTIAL,
  ).length
  const wrongCount = isEmpty(answersReview)
    ? attempt?.wrongCount ?? 0
    : answersReview.filter(
        (question) => question?.result === ANSWERS_RESULT_INCORRECT,
      ).length
  const unansweredCount = answersReview.filter(
    (question) => question?.result === ANSWERS_RESULT_UNANSWERED,
  ).length

  return (
    <Box className={classes.attemptDetailsContent}>
      <Box className={classes.summaryRow}>
        <Box className={classes.summaryLeft}>
          <Box className={classes.attemptCircle}>
            <Typography className={classes.attemptNumber}>
              {attempt?.correctCount ?? 0}
            </Typography>
            <Typography className={classes.attemptOf}>
              de {attempt?.totalQuestions ?? 0}
            </Typography>
          </Box>
          <Box>
            <Typography className={classes.summaryTitle}>
              {summaryTitle}
            </Typography>
            <Typography className={classes.summaryScore}>
              Você acertou {attemptScore}% das Questões
            </Typography>
          </Box>
        </Box>
        <Box className={classes.statsRow}>
          <Box className={clsx(classes.statBox, classes.statBoxTotal)}>
            <Typography className={classes.statLabel}>Total</Typography>
            <Typography className={classes.statValue}>
              {totalQuestions}
            </Typography>
          </Box>
          <Box className={clsx(classes.statBox, classes.statBoxCorrect)}>
            <Typography className={classes.statLabel}>Acertos</Typography>
            <Typography className={classes.statValue}>
              {correctCount}
            </Typography>
          </Box>
          {partialCount > 0 && (
            <Box className={clsx(classes.statBox, classes.statBoxPartial)}>
              <Typography className={classes.statLabel}>Parciais</Typography>
              <Typography className={classes.statValue}>
                {partialCount}
              </Typography>
            </Box>
          )}
          <Box className={clsx(classes.statBox, classes.statBoxWrong)}>
            <Typography className={classes.statLabel}>Erros</Typography>
            <Typography className={classes.statValue}>{wrongCount}</Typography>
          </Box>
          {unansweredCount > 0 && (
            <Box className={clsx(classes.statBox, classes.statBoxUnanswered)}>
              <Typography className={classes.statLabel}>
                Não respondidas
              </Typography>
              <Typography className={classes.statValue}>
                {unansweredCount}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {!isEmpty(answersReview) ? (
        answersReview.map((questionReview, index) => {
          const result = questionReview?.result
          const isCorrect = result === ANSWERS_RESULT_CORRECT
          const isPartial = result === ANSWERS_RESULT_PARTIAL
          const isUnanswered = result === ANSWERS_RESULT_UNANSWERED
          const isWrong = !isCorrect && !isPartial && !isUnanswered

          const selectedIds =
            questionReview?.selectedAlternativeIds ??
            (questionReview?.selectedAlternativeId
              ? [questionReview.selectedAlternativeId]
              : [])

          return (
            <Box
              key={questionReview?.questionId}
              className={clsx(classes.questionCard, {
                [classes.questionCardCorrect]: isCorrect,
                [classes.questionCardPartial]: isPartial,
                [classes.questionCardWrong]: isWrong,
                [classes.questionCardUnanswered]: isUnanswered,
              })}
            >
              {/* Header */}
              <Box
                className={clsx(classes.cardHeader, {
                  [classes.cardHeaderCorrect]: isCorrect,
                  [classes.cardHeaderPartial]: isPartial,
                  [classes.cardHeaderWrong]: isWrong,
                  [classes.cardHeaderUnanswered]: isUnanswered,
                })}
              >
                {isCorrect && <CheckCircle className={classes.iconCorrect} />}
                {isPartial && <Stars className={classes.iconPartial} />}
                {isWrong && <Cancel className={classes.iconWrong} />}
                {isUnanswered && (
                  <RemoveCircle className={classes.iconUnanswered} />
                )}
                <Typography
                  className={clsx(classes.statusLabel, {
                    [classes.statusLabelCorrect]: isCorrect,
                    [classes.statusLabelPartial]: isPartial,
                    [classes.statusLabelWrong]: isWrong,
                    [classes.statusLabelUnanswered]: isUnanswered,
                  })}
                >
                  {isCorrect && 'Resposta Correta'}
                  {isPartial && 'Resposta Parcial'}
                  {isWrong && 'Resposta Incorreta'}
                  {isUnanswered && 'Não Respondida'}
                </Typography>
              </Box>

              {/* Two-column body */}
              <Box className={classes.cardBody}>
                {/* Left: statement + explanation */}
                <Box className={classes.leftColumn}>
                  <Typography className={classes.questionStatement}>
                    {index + 1}. {questionReview?.statement}
                  </Typography>

                  {questionReview?.explanation && !isUnanswered && (
                    <Box
                      className={clsx(classes.explanationBox, {
                        [classes.explanationBoxCorrect]: isCorrect,
                        [classes.explanationBoxPartial]: isPartial,
                        [classes.explanationBoxWrong]: isWrong,
                        [classes.explanationBoxUnanswered]: isUnanswered,
                      })}
                    >
                      <Typography
                        className={clsx(classes.explanationTitle, {
                          [classes.explanationTitleCorrect]: isCorrect,
                          [classes.explanationTitlePartial]: isPartial,
                          [classes.explanationTitleWrong]: isWrong,
                          [classes.explanationTitleUnanswered]: isUnanswered,
                        })}
                      >
                        {isCorrect && 'Resposta correta!'}
                        {isPartial && 'Resposta parcial'}
                        {isWrong && 'Resposta Incorreta!'}
                        {isUnanswered && 'Questão não respondida'}
                      </Typography>
                      <Typography className={classes.explanationText}>
                        {questionReview?.explanation}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Right: alternatives */}
                <Box className={classes.rightColumn}>
                  {questionReview?.alternatives?.map(
                    (alternative, altIndex) => {
                      const isSelected = selectedIds.includes(alternative?.id)
                      const isAltCorrect = alternative?.correct && !isUnanswered

                      return (
                        <Box
                          key={alternative?.id}
                          className={clsx(classes.alternativeRow, {
                            [classes.alternativeRowCorrect]: isAltCorrect,
                            [classes.alternativeRowWrong]:
                              isSelected && !isAltCorrect,
                          })}
                        >
                          <Box
                            className={clsx(classes.alternativeLetter, {
                              [classes.alternativeLetterCorrect]: isAltCorrect,
                              [classes.alternativeLetterWrong]:
                                isSelected && !isAltCorrect,
                            })}
                          >
                            {LETTERS[altIndex]}
                          </Box>
                          <Typography className={classes.alternativeContent}>
                            {alternative?.content}
                          </Typography>
                        </Box>
                      )
                    },
                  )}
                </Box>
              </Box>
            </Box>
          )
        })
      ) : (
        <Alert severity="info" className={classes.disabledReviewAlert}>
          <AlertTitle>Revisão detalhada</AlertTitle>A revisão detalhada das
          respostas não está disponível para este curso. Essa opção pode ser
          ativada pelo administrador.
        </Alert>
      )}
    </Box>
  )
}

AttemptDetails.propTypes = {
  attempt: PropTypes.object,
  summary: PropTypes.node,
}

export default AttemptDetails
